import type { ChatConfig, UiTexts } from '../types/chat'
import { detectLocale, pickLocale } from './locale'

/**
 * Built-in interface copy (labels for screen readers, status, system
 * messages). German and English ship by default; integrators can add or
 * override locales field by field via `uiTexts`. `{name}` is the bot name.
 */
export const UI_DEFAULT_TEXTS: Record<string, UiTexts> = {
  de: {
    openChat: 'Chat mit {name} öffnen',
    closeChat: 'Chat schließen',
    panelLabel: 'Chat mit {name}',
    messagesLabel: 'Gesprächsverlauf',
    inputLabel: 'Nachricht an {name}',
    placeholder: 'Ihre Nachricht …',
    send: 'Senden',
    status: 'Online',
    typing: '{name} schreibt …',
    emptyResponse: 'Darauf habe ich gerade keine Antwort erhalten. Bitte versuchen Sie es noch einmal.',
    sessionExpired: 'Die Sitzung wurde nach längerer Pause beendet. Sie können neu beginnen.',
    you: 'Sie',
    welcome: 'Hallo! Wie kann ich helfen?',
    error: 'Die Verbindung hat gerade nicht geklappt. Bitte versuchen Sie es noch einmal.',
  },
  en: {
    openChat: 'Open chat with {name}',
    closeChat: 'Close chat',
    panelLabel: 'Chat with {name}',
    messagesLabel: 'Conversation',
    inputLabel: 'Message to {name}',
    placeholder: 'Type your message…',
    send: 'Send',
    status: 'Online',
    typing: '{name} is typing…',
    emptyResponse: "I didn't receive an answer just now. Please try again.",
    sessionExpired: 'Session expired due to inactivity. Starting a new session.',
    you: 'You',
    welcome: 'Hi! How can I help you?',
    error: "Sorry, I couldn't reach the server. Please try again.",
  },
}

export function resolveUiTexts(config: ChatConfig): UiTexts {
  const locale = detectLocale(config.locale)
  const base = pickLocale(locale, UI_DEFAULT_TEXTS, UI_DEFAULT_TEXTS.en)
  const user = pickLocale(locale, config.uiTexts, {})
  const name = config.botName ?? ''
  const out = { ...base, ...user } as UiTexts
  for (const k of Object.keys(out) as (keyof UiTexts)[]) out[k] = out[k].split('{name}').join(name)
  return out
}

/** Time of a message in the page's locale (24 h for de, browser default otherwise). */
export function formatTime(ts: number, locale: string): string {
  try {
    return new Date(ts).toLocaleTimeString(locale || undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }
}
